/**
 * @format
 * @module utils/detect
 * @description Please keep these values in sync with resources/styles/layouts/_tiers.scss
 */

/**
 * @readonly
 * @type {Object.<String, { px: Number, rem: Number }>}
 * @description Order matters here. Objects should be in width-ascending order!
 * The `px` values are used for Google Ads (adslot-size-mapping.js)
 * The `rem` values are used for window.matchMedia() queries
 */
export const breakpoints = {
  // gridXS: { px: 0, rem: 0 }, // tgam-flexboxgrid XS (Not really needed)
  mobile: { px: 320, rem: 20 },
  mobileMedium: { px: 360, rem: 22.5 },
  mobileWide: { px: 570, rem: 35.62 },
  tablet: { px: 768, rem: 48 }, // equals tgam-flexboxgrid SM
  tabletMedium: { px: 980, rem: 61.25 }, // equals tgam-flexboxgrid MD
  tabletWide: { px: 1024, rem: 64 },
  desktop: { px: 1280, rem: 80 }, // equals tgam-flexboxgrid LG
  desktopWide: { px: 1400, rem: 87.5 }
};

/** @type {Number} */
const REM_OFFSET = 0.01; // value aligns with tgam-flexbox-grid and sass-mq offset

/**
 * Get the breakpoint object for the supplied name
 * @param {String} name - breakpoint name to retrieve
 * @returns {{ px: Number, rem: Number } | null}
 */
export function getBreakpointName(name) {
  return breakpoints[name] || null;
}

/**
 * Check if user-agent string is iPhone or iPad or iPod
 * @returns {Boolean}
 */
export function isIOS() {
  return navigator.userAgent.match(/iPhone|iPad|iPod/i) !== null;
}

/**
 * Check if user-agent string is iPhone or iPad
 * @returns {Boolean}
 */
export function isIphone() {
  return navigator.userAgent.match(/iPhone|iPod/i) !== null;
}

/**
 * Check if user-agent string is iPad
 * @returns {Boolean}
 */
export function isIpad() {
  return navigator.userAgent.match(/iPad/i) !== null;
}

/**
 * Check if user-agent string is Android
 * @returns {Boolean}
 */
export function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

/**
 * Check if browser is Android (user-agent) and Tablet viewport
 * @returns {Boolean}
 */
export function isAndroidTablet() {
  const androidMobile =
    navigator.userAgent.match(/Android/i) && !navigator.userAgent.match(/Mobile/i);
  return androidMobile && isTablet();
}

/**
 * Check if user-agent string is Blackberry
 * @returns {Boolean}
 */
export function isBlackBerry() {
  return navigator.userAgent.match(/BlackBerry/i) !== null;
}

/**
 * Check if user-agent string is FB App value
 * @returns {Boolean}
 */
export function isFacebookApp() {
  return navigator.userAgent.indexOf("FBAN/") > -1;
}

/**
 * Check if user-agent string is Twitter
 * @returns {Boolean}
 */
export function isTwitterApp() {
  // NB Android app is indistinguishable from Chrome: http://mobiforge.com/research-analysis/webviews-and-user-agent-strings
  return navigator.userAgent.indexOf("Twitter for iPhone") > -1;
}

/**
 * Check if the document referrer is Twitter domain.
 * @returns {Boolean}
 */
export function isTwitterReferral() {
  return /\.t\.co/.test(document.referrer);
}

/**
 * Check if the document referrer is FB domain.
 * @returns {Boolean}
 */
export function isFacebookReferral() {
  return /\.facebook\.com/.test(document.referrer);
}

/**
 * Get the viewport orientation string.
 * @returns {"portrait" | "landscape"}
 */
export function getOrientation() {
  const vp = getViewport();
  return vp.height > vp.width ? "portrait" : "landscape";
}

/**
 * Get the viewport width and height in pixels.
 * @returns {{ width: Number, height: Number }}
 */
export function getViewport() {
  const w = window;
  const d = document;
  const e = d.documentElement;
  const g = d.body;
  return {
    width: w.innerWidth || e.clientWidth || g.clientWidth,
    height: w.innerHeight || e.clientHeight || g.clientHeight
  };
}

/**
 * Detect if the browser has "pointer" (mouse or touch-pad) input support
 * @see https://medium.com/@ferie/detect-a-touch-device-with-only-css-9f8e30fa1134
 * @returns {Boolean}
 */
export function hasMouseSupport() {
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * Detect if the viewport is mobile width (excludes tablet and desktop).
 * @param {Boolean} [checkUserAgent="true"] - check browser's user-agent string for OS strings?
 * @returns {Boolean}
 */
export function isMobile(checkUserAgent = true) {
  const isTabletDown = isBreakpointDown("tablet");
  if (checkUserAgent) {
    const isMobileUA =
      isIOS() ||
      (isAndroid() && !isAndroidTablet()) ||
      isBlackBerry() ||
      isFacebookApp() ||
      isTwitterApp();
    return isMobileUA && isTabletDown;
  }
  return isTabletDown;
}

/**
 * Detect if the viewport is below mobileWide width
 * @param {String} [breakpoint="mobileWide"] - breakpoint name
 * @returns {Boolean}
 */
export function isMobileWide(breakpoint = "mobileWide") {
  return isBreakpointDown(breakpoint);
}

/**
 * Detect if the viewport is tablet width (excludes desktop).
 * @param {String} [breakpoint="tablet"] - breakpoint name
 * @returns {Boolean}
 */
export function isTablet(breakpoint = "tablet") {
  return isBreakpointBetween(breakpoint, "desktop");
}

/**
 * Detect if the viewport is tablet landscape orientation
 * @returns {Boolean}
 */
export function isTabletLandscape() {
  return getOrientation() === "landscape" && isTablet("tabletWide");
}

/**
 * Detect if the viewport is tablet portrait orientation
 * @returns {Boolean}
 */
export function isTabletPortrait() {
  return getOrientation() === "portrait" && isTablet("tablet");
}

/**
 * Detect if the viewport is tablet width or wider.
 * @param {String} [breakpoint="tablet"] - breakpoint name
 * @returns {Boolean}
 */
export function isTabletUp(breakpoint = "tablet") {
  return isBreakpointUp(breakpoint);
}

/**
 * Detect if the viewport is desktop width or wider. Alternate name: isDesktopUp()
 * @param {String} [breakpoint="desktop"] - breakpoint name
 * @returns {Boolean}
 */
export function isDesktop(breakpoint = "desktop") {
  return isBreakpointUp(breakpoint);
}

/**
 * Detect if the viewport is breakpoint wide or wider.
 * @param {String} breakpoint - breakpoint name
 * @returns {Boolean}
 */
function isBreakpointUp(breakpoint) {
  const minBp = getBreakpointName(breakpoint);
  if (!minBp) {
    return false;
  }
  return window.matchMedia(`(min-width: ${minBp.rem}rem)`).matches;
}

/**
 * Detect if the viewport is smaller than breakpoint.
 * @param {String} breakpoint - breakpoint name
 * @returns {Boolean}
 */
function isBreakpointDown(breakpoint) {
  const maxBp = getBreakpointName(breakpoint);
  if (!maxBp) {
    return false;
  }
  return window.matchMedia(`(max-width: ${maxBp.rem - REM_OFFSET}rem)`).matches;
}

/**
 * Detect if the viewport is within min and max breakpoint range.
 * @param {String} minBreakpoint - breakpoint name
 * @param {String} maxBreakpoint - breakpoint name
 * @returns {Boolean}
 */
export function isBreakpointBetween(minBreakpoint, maxBreakpoint) {
  const minBp = getBreakpointName(minBreakpoint);
  const maxBp = getBreakpointName(maxBreakpoint);
  if (!minBp || !maxBp) {
    return false;
  }
  return window.matchMedia(
    `(min-width: ${minBp.rem}rem) and (max-width: ${maxBp.rem - REM_OFFSET}rem)`
  ).matches;
}

/**
 * Get the current viewport bucket name
 * @returns {String}
 */
export function getDeviceBucket() {
  if (isDesktop()) {
    return "desktop";
  }
  if (isTablet()) {
    const landscape = isTabletLandscape();
    if (landscape) {
      return "tabletWide";
    }
    return "tablet";
  }
  // mobile and catch all
  return "mobile";
}

/**
 * Get the browser's name and version based on the user-agent string.
 * Please note, this method may not work for some browsers. For example, for Chrome mobile it will return "Safari 11".
 * Alternatively you can user ua-parser-js library https://github.com/faisalman/ua-parser-js, see examples in the codebase
 * @see https://github.com/guardian/frontend/blob/cd09ba0cf23561f6b8adc6da4eda4e07c80b3f61/static/src/javascripts/projects/common/utils/detect.js#L152
 * @returns {{ browser: String, version: String }}
 */
export const getUserAgent = (() => {
  var ua = navigator.userAgent;
  var tem;
  var M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  if (/trident/i.test(M[1])) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return "IE " + (tem[1] || "");
  }
  if (M[1] === "Chrome" && /Edge/i.test(ua)) {
    return {
      browser: "Edge",
      version: /Edge\/(\d+)/.exec(ua)[1]
    };
  }
  if (M[1] === "Chrome") {
    tem = ua.match(/\bOPR\/(\d+)/);
    if (tem !== null) {
      return "Opera " + tem[1];
    }
  }
  M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
  if ((tem = ua.match(/version\/(\d+)/i)) !== null) {
    M.splice(1, 1, tem[1]);
  }
  return {
    browser: M[0],
    version: M[1]
  };
})();
